import { authHeaders } from './session';

/**
 * The API client: hand-written types mirroring the API's responses, in one
 * file, so there is one place to replace if contracts are ever generated.
 *
 * TWO CALL PATHS, AND THEY MUST NOT MIX. lasti.fi shipped four writes through
 * its GET helper: a fetch init passed as query parameters typechecks (both are
 * strings) and turned into `?method=PUT&body={"value":"rk_live_…"}` — a secret
 * in a URL. So `request` is GET-only and its params type forbids `method` and
 * `body`; writes go through `send`, which takes a real init. Do not widen it.
 */

export class ApiError extends Error {
  readonly status: number;
  /** The API's stable error code: `subscription-required`, `validation-failed`, … */
  readonly code: string;
  readonly body: Record<string, unknown> | undefined;

  constructor(status: number, code: string, message: string, body?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

type QueryValue = string | number | boolean | undefined;
type Query = Record<string, QueryValue> & { method?: never; body?: never };

async function parse<T>(response: Response): Promise<T> {
  const text = await response.text();
  const body = text ? (JSON.parse(text) as Record<string, unknown>) : undefined;
  if (!response.ok) {
    throw new ApiError(
      response.status,
      typeof body?.code === 'string' ? body.code : 'error',
      typeof body?.message === 'string' ? body.message : response.statusText,
      body,
    );
  }
  return body as T;
}

export async function request<T>(path: string, params: Query = {}): Promise<T> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) query.set(key, String(value));
  }
  const qs = query.toString();
  const response = await fetch(`${path}${qs ? `?${qs}` : ''}`, { headers: authHeaders() });
  return parse<T>(response);
}

export async function send<T>(
  path: string,
  init: { method: 'POST' | 'PATCH' | 'PUT' | 'DELETE'; body?: unknown },
): Promise<T> {
  const response = await fetch(path, {
    method: init.method,
    headers: {
      ...authHeaders(),
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  return parse<T>(response);
}

// --- endpoints --------------------------------------------------------------

export type Locale = 'fi' | 'en';

export interface Me {
  id: string;
  username: string | null;
  displayName: string | null;
  email: string | null;
  locale: Locale;
  isPlatformAdmin: boolean;
  entitlement: { active: boolean; until: string | null; source: string | null };
}

export interface ProjectListItem {
  org: { slug: string; name: string; verified: boolean };
  slug: string;
  key: string;
  name: string;
  visibility: 'public' | 'unlisted' | 'members';
  role: 'admin' | 'maintainer' | 'member' | null;
  theme: { light?: { primary?: string }; dark?: { primary?: string } } | null;
  /** The project's icon URL, or null for the letter mark. */
  icon: string | null;
}

export type IssueType = 'bug' | 'feature' | 'question';
export type IssueStatus =
  'triage' | 'backlog' | 'planned' | 'in_progress' | 'done' | 'wont_do' | 'duplicate';

export const OPEN_STATUSES: readonly IssueStatus[] = [
  'triage',
  'backlog',
  'planned',
  'in_progress',
];
export const CLOSED_STATUSES: readonly IssueStatus[] = ['done', 'wont_do', 'duplicate'];

/** As stored: a partial palette per mode (@sulje/brand `ThemeOverride`). */
export type ThemeLayer = { light?: Record<string, string>; dark?: Record<string, string> } | null;

export interface Project {
  org: { slug: string; name: string; verified: boolean; theme: ThemeLayer };
  slug: string;
  key: string;
  name: string;
  descriptionMd: string;
  visibility: 'public' | 'unlisted' | 'members';
  posting: 'everyone' | 'members';
  enabledTypes: IssueType[];
  roadmapEnabled: boolean;
  theme: ThemeLayer;
  archived: boolean;
  role: 'admin' | 'maintainer' | 'member' | null;
  canPost: boolean;
  isMaintainer: boolean;
  /** May change the project's icon (migration 0010). */
  isAdmin: boolean;
  icon: string | null;
}

/** What anyone may see about a PUBLIC project (migration 0009). */
export interface PublicProject {
  org: { slug: string; name: string; verified: boolean; theme: ThemeLayer };
  slug: string;
  key: string;
  name: string;
  descriptionMd: string;
  theme: ThemeLayer;
  enabledTypes: IssueType[];
  openIssues: number;
  closedIssues: number;
  icon: string | null;
}

export interface Person {
  username: string | null;
  displayName: string | null;
  deleted: boolean;
}

export interface IssueListItem {
  key: string;
  number: number;
  type: IssueType;
  status: IssueStatus;
  title: string;
  private: boolean;
  locked: boolean;
  voteCount: number;
  commentCount: number;
  createdAt: string;
  updatedAt: string;
  reporter: Person;
  voted: boolean;
}

export interface IssueComment {
  id: string;
  /** Null for a hidden comment, unless you are a maintainer. */
  bodyMd: string | null;
  hiddenReason: string | null;
  acceptedAnswer: boolean;
  editedAt: string | null;
  createdAt: string;
  author: Person;
  mine: boolean;
}

export interface Issue extends IssueListItem {
  bodyMd: string;
  closedAt: string | null;
  mine: boolean;
  canComment: boolean;
  canTriage: boolean;
  comments: IssueComment[];
}

export interface IssueFilters {
  state: 'open' | 'closed';
  type?: IssueType | undefined;
  sort: 'votes' | 'new' | 'updated';
}

export interface ProjectMember {
  id: string;
  kind: 'person' | 'team';
  role: 'maintainer' | 'member';
  name: string | null;
  username: string | null;
  /** Only maintainers see addresses. */
  email: string | null;
  since: string;
}

export interface PendingInvite {
  id: string;
  email: string;
  role: 'maintainer' | 'member';
  expiresAt: string;
  createdAt: string;
}

export interface MyInvite {
  id: string;
  org: { slug: string; name: string };
  project: { slug: string; name: string } | null;
  role: string;
  invitedBy: string | null;
  expiresAt: string;
}

const projectPath = (org: string, project: string) =>
  `/v1/projects/${encodeURIComponent(org)}/${encodeURIComponent(project)}`;

export const api = {
  me: () => request<Me>('/v1/me'),
  updateMe: (changes: { locale?: Locale; username?: string }) =>
    send<Me>('/v1/me', { method: 'PATCH', body: changes }),
  projects: () => request<{ items: ProjectListItem[] }>('/v1/projects'),
  project: (org: string, project: string) => request<Project>(projectPath(org, project)),
  issues: (org: string, project: string, filters: IssueFilters) =>
    request<{ counts: { open: number; closed: number }; items: IssueListItem[] }>(
      `${projectPath(org, project)}/issues`,
      { state: filters.state, type: filters.type, sort: filters.sort },
    ),
  issue: (org: string, project: string, number: number) =>
    request<Issue>(`${projectPath(org, project)}/issues/${number}`),
  fileIssue: (
    org: string,
    project: string,
    input: { type: IssueType; title: string; body: string; private: boolean },
  ) =>
    send<{ key: string; number: number }>(`${projectPath(org, project)}/issues`, {
      method: 'POST',
      body: input,
    }),
  comment: (org: string, project: string, number: number, body: string) =>
    send<{ id: string }>(`${projectPath(org, project)}/issues/${number}/comments`, {
      method: 'POST',
      body: { body },
    }),
  vote: (org: string, project: string, number: number, on: boolean) =>
    send<null>(`${projectPath(org, project)}/issues/${number}/vote`, {
      method: on ? 'PUT' : 'DELETE',
    }),
  members: (org: string, project: string) =>
    request<{
      canManage: boolean;
      canGrant: boolean;
      members: ProjectMember[];
      invites: PendingInvite[];
    }>(`${projectPath(org, project)}/members`),
  invite: (
    org: string,
    project: string,
    input: { email: string; role: 'maintainer' | 'member'; grant?: { reason: string } },
  ) =>
    send<{ id: string; link: string }>(`${projectPath(org, project)}/invites`, {
      method: 'POST',
      body: input,
    }),
  revokeInvite: (org: string, project: string, id: string) =>
    send<null>(`${projectPath(org, project)}/invites/${id}`, { method: 'DELETE' }),
  setMemberRole: (org: string, project: string, id: string, role: 'maintainer' | 'member') =>
    send<{ role: string }>(`${projectPath(org, project)}/members/${id}`, {
      method: 'PATCH',
      body: { role },
    }),
  /** The image itself as the body, already resized (lib/icon.ts). */
  setIcon: async (org: string, project: string, image: Blob) =>
    parse<{ icon: string }>(
      await fetch(`${projectPath(org, project)}/icon`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': image.type },
        body: image,
      }),
    ),
  removeIcon: (org: string, project: string) =>
    send<null>(`${projectPath(org, project)}/icon`, { method: 'DELETE' }),
  removeMember: (org: string, project: string, id: string) =>
    send<null>(`${projectPath(org, project)}/members/${id}`, { method: 'DELETE' }),
  myInvites: () => request<{ items: MyInvite[] }>('/v1/invites'),
  billing: () =>
    request<{
      available: boolean;
      hasCustomer: boolean;
      /** `endsAt`: when it ends instead of renewing; null while it renews. */
      subscription: { status: string; currentPeriodEnd: string; endsAt: string | null } | null;
    }>('/v1/billing'),
  /** `returnTo`: a path on this site to land on after paying. */
  checkout: (returnTo?: string) =>
    send<{ url: string }>('/v1/billing/checkout', {
      method: 'POST',
      body: returnTo ? { returnTo } : {},
    }),
  publicProject: (org: string, project: string) =>
    request<PublicProject>(
      `/v1/public/projects/${encodeURIComponent(org)}/${encodeURIComponent(project)}`,
    ),
  portal: () => send<{ url: string }>('/v1/billing/portal', { method: 'POST' }),
  acceptInvite: (id: string) =>
    send<{ org: string; project: string | null }>(`/v1/invites/${id}/accept`, { method: 'POST' }),
  setStatus: (org: string, project: string, number: number, status: IssueStatus) =>
    send<{ status: IssueStatus }>(`${projectPath(org, project)}/issues/${number}`, {
      method: 'PATCH',
      body: { status },
    }),
};
